import * as React from "react";
import { Link } from "react-router-dom";
import { gettext } from "../../i18n";
import "./MapLegend.scss";

export interface ConflictingTiltStation {
  id: number;
  identifier: string;
}

interface MapLegendProps {
  showGnss: boolean;
  showTiltmeter: boolean;
  tiltAxes?: TiltAxisConfig[];
  conflictingTiltStations?: ConflictingTiltStation[];
}

export interface TiltAxisConfig {
  label: string;
  color: string;
  angle: number;
  intoGround?: boolean;
}

export interface TiltAxisDirection {
  label: string;
  angle: number;
  intoGround?: boolean;
}

export interface TiltAxisLayout {
  id: string;
  label: string;
  directions: TiltAxisDirection[];
  defaultAngles: Record<string, number>;
}

export const DEFAULT_TILT_AXIS_LAYOUT_ID = "3d";

export const TILT_AXIS_LAYOUTS: Record<string, TiltAxisLayout> = {
  "3d": {
    id: "3d",
    label: "3D",
    directions: [
      { label: "North-west", angle: 210 },
      { label: "Up", angle: -90 },
      { label: "North-east", angle: -30 },
    ],
    defaultAngles: {
      X: -30,
      Y: -90,
      Z: 210,
    },
  },
  "2d": {
    id: "2d",
    label: "2D",
    directions: [
      { label: "West", angle: 180 },
      { label: "South", angle: 90 },
      { label: "Down", angle: 0, intoGround: true },
    ],
    defaultAngles: {
      X: 0,
      Y: 90,
      Z: 180,
    },
  },
};

export const TILT_AXIS_META: { label: string; color: string }[] = [
  { label: "X", color: "#f97316" },
  { label: "Y", color: "#0f766e" },
  { label: "Z", color: "#4f46e5" },
];

export const getTiltAxisLayout = (layoutId?: string | null): TiltAxisLayout =>
  (layoutId && TILT_AXIS_LAYOUTS[layoutId]) ||
  TILT_AXIS_LAYOUTS[DEFAULT_TILT_AXIS_LAYOUT_ID];

export const getEffectiveTiltAngles = (
  layoutId: string | null | undefined,
  savedAngles: Record<string, number> | null | undefined,
): Record<string, number> => {
  const layout = getTiltAxisLayout(layoutId);
  const angles: Record<string, number> = {};
  for (const axis of TILT_AXIS_META) {
    const configured = savedAngles?.[axis.label];
    angles[axis.label] =
      typeof configured === "number"
        ? configured
        : layout.defaultAngles[axis.label];
  }
  return angles;
};

const DEFAULT_TILT_AXES: TiltAxisConfig[] = TILT_AXIS_META.map((axis) => ({
  ...axis,
  angle:
    TILT_AXIS_LAYOUTS[DEFAULT_TILT_AXIS_LAYOUT_ID].defaultAngles[axis.label],
}));

const GNSS_SCALE_ITEMS = [
  { label: gettext("0-5 mm"), color: "#22c55e" },
  { label: gettext("5-10 mm"), color: "#eab308" },
  { label: gettext("10+ mm"), color: "#ef4444" },
];

const circularAngleDistance = (angleA: number, angleB: number): number => {
  const diff = Math.abs(angleA - angleB) % 360;
  return Math.min(diff, 360 - diff);
};

// Picks the label position for an into-ground axis: the angle farthest from
// all arrow axes, so the label never collides with another axis.
const findIntoGroundLabelAngle = (otherAngles: number[]): number => {
  let bestAngle = 0;
  let bestScore = -1;
  for (let candidate = 0; candidate < 360; candidate += 15) {
    const score =
      otherAngles.length === 0
        ? 360
        : Math.min(
            ...otherAngles.map((other) =>
              circularAngleDistance(candidate, other),
            ),
          );
    if (score > bestScore) {
      bestScore = score;
      bestAngle = candidate;
    }
  }
  return bestAngle;
};

const TiltAxisDiagram: React.FC<{ axes?: TiltAxisConfig[] }> = ({
  axes = DEFAULT_TILT_AXES,
}) => {
  const size = 72;
  const center = size / 2;
  const armLength = 24;
  const labelOffset = 9;
  const labelRadius = armLength + labelOffset;
  const intoGroundLabelRadius = 16;
  const tipLength = 5;
  const textMargin = 6;
  const fitPadding = 1;

  const arrowAxes = axes.filter((axis) => !axis.intoGround);
  const intoGroundAxes = axes.filter((axis) => axis.intoGround);
  const intoGroundLabelAngle = findIntoGroundLabelAngle(
    arrowAxes.map((axis) => axis.angle),
  );

  // Fit the content into the frame: center it, scale down if it overflows.
  const bounds = {
    minX: center - 2.4,
    minY: center - 2.4,
    maxX: center + 2.4,
    maxY: center + 2.4,
  };
  const expandBounds = (x: number, y: number) => {
    bounds.minX = Math.min(bounds.minX, x);
    bounds.minY = Math.min(bounds.minY, y);
    bounds.maxX = Math.max(bounds.maxX, x);
    bounds.maxY = Math.max(bounds.maxY, y);
  };
  const addLabelBounds = (radius: number, angle: number) => {
    const rad = (angle * Math.PI) / 180;
    const x = center + radius * Math.cos(rad);
    const y = center + radius * Math.sin(rad);
    expandBounds(x - textMargin, y - textMargin);
    expandBounds(x + textMargin, y + textMargin);
  };

  for (const axis of arrowAxes) {
    const rad = (axis.angle * Math.PI) / 180;
    expandBounds(
      center + (armLength + tipLength) * Math.cos(rad),
      center + (armLength + tipLength) * Math.sin(rad),
    );
    addLabelBounds(labelRadius, axis.angle);
  }
  if (intoGroundAxes.length > 0) {
    expandBounds(center - 6, center - 6);
    expandBounds(center + 6, center + 6);
    addLabelBounds(intoGroundLabelRadius, intoGroundLabelAngle);
  }

  const contentWidth = bounds.maxX - bounds.minX;
  const contentHeight = bounds.maxY - bounds.minY;
  const scale =
    contentWidth <= 0 || contentHeight <= 0
      ? 1
      : Math.min(
          1,
          (size - fitPadding * 2) / contentWidth,
          (size - fitPadding * 2) / contentHeight,
        );
  const translateX = center - (scale * (bounds.minX + bounds.maxX)) / 2;
  const translateY = center - (scale * (bounds.minY + bounds.maxY)) / 2;

  return (
    <svg
      className="tilt-axis-diagram"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g transform={`translate(${translateX} ${translateY}) scale(${scale})`}>
        <circle cx={center} cy={center} r="2.4" fill="#1f2937" opacity="0.4" />
        {arrowAxes.map((axis) => {
          const rad = (axis.angle * Math.PI) / 180;
          const endX = center + armLength * Math.cos(rad);
          const endY = center + armLength * Math.sin(rad);

          const arrowSize = 4;
          const tipX = center + (armLength + tipLength) * Math.cos(rad);
          const tipY = center + (armLength + tipLength) * Math.sin(rad);
          const arrowAngle1 = rad + (150 * Math.PI) / 180;
          const arrowAngle2 = rad - (150 * Math.PI) / 180;
          const a1x = endX + arrowSize * Math.cos(arrowAngle1);
          const a1y = endY + arrowSize * Math.sin(arrowAngle1);
          const a2x = endX + arrowSize * Math.cos(arrowAngle2);
          const a2y = endY + arrowSize * Math.sin(arrowAngle2);

          const labelX = center + labelRadius * Math.cos(rad);
          const labelY = center + labelRadius * Math.sin(rad);

          return (
            <g key={axis.label}>
              <line
                x1={center}
                y1={center}
                x2={endX}
                y2={endY}
                stroke={axis.color}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <polygon
                points={`${tipX},${tipY} ${a1x},${a1y} ${a2x},${a2y}`}
                fill={axis.color}
              />
              <text
                x={labelX}
                y={labelY}
                fill={axis.color}
                fontSize="11"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {axis.label}
              </text>
            </g>
          );
        })}
        {intoGroundAxes.map((axis) => {
          const rad = (intoGroundLabelAngle * Math.PI) / 180;
          const labelX = center + intoGroundLabelRadius * Math.cos(rad);
          const labelY = center + intoGroundLabelRadius * Math.sin(rad);

          return (
            <g key={axis.label}>
              <circle
                cx={center}
                cy={center}
                r="6"
                fill="none"
                stroke={axis.color}
                strokeWidth="2"
              />
              <circle cx={center} cy={center} r="2.2" fill={axis.color} />
              <text
                x={labelX}
                y={labelY}
                fill={axis.color}
                fontSize="11"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {axis.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

const MapLegend: React.FC<MapLegendProps> = ({
  showGnss,
  showTiltmeter,
  tiltAxes,
  conflictingTiltStations,
}) => {
  if (!showGnss && !showTiltmeter) {
    return null;
  }

  const isTiltLayout2d = tiltAxes?.some((axis) => axis.intoGround) ?? false;

  return (
    <div className="map-legend">
      {showGnss && (
        <div className="map-legend-block">
          <div className="map-legend-title">{gettext("GNSS movement")}</div>
          {GNSS_SCALE_ITEMS.map((item) => (
            <div key={item.label} className="map-legend-row">
              <span
                className="map-legend-swatch"
                style={{ backgroundColor: item.color }}
              />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
      {showTiltmeter && (
        <div
          className={`map-legend-block map-legend-block--tilt${
            isTiltLayout2d ? " map-legend-block--tilt-2d" : ""
          }`}
        >
          <div className="map-legend-title">{gettext("Tilt axes")}</div>
          <TiltAxisDiagram axes={tiltAxes} />
        </div>
      )}
      {showTiltmeter &&
        conflictingTiltStations &&
        conflictingTiltStations.length > 0 && (
          <div className="map-legend-block map-legend-block--conflict">
            <span className="map-legend-conflict-label">
              {gettext(
                "The following stations have conflicting axis configurations:",
              )}{" "}
            </span>
            {conflictingTiltStations.map((station, index) => (
              <React.Fragment key={station.id}>
                {index > 0 ? ", " : ""}
                <Link to={`/stations/${station.identifier}`}>
                  {station.identifier}
                </Link>
              </React.Fragment>
            ))}
          </div>
        )}
    </div>
  );
};

export default MapLegend;
